import { supabase } from '../lib/supabase';

interface SyncJunctionTableParams {
  table: string;
  fixedColumn: string;
  fixedValue: string;
  varyingColumn: string;
  previousIds: string[];
  nextIds: string[];
  organizationId: string | null;
}

// Diffs `previousIds` against `nextIds` and applies only the delta (delete removed rows, insert
// added rows) against a two-column-plus-organization_id junction table — e.g. category_options,
// machine_options. Diffing (rather than delete+reinsert) avoids a spurious created_at churn on
// unrelated rows that didn't actually change. Shared by useManageCategories, useManageOptions,
// and useMachines so this exact operation can't drift between the three call sites.
export async function syncJunctionTable({
  table,
  fixedColumn,
  fixedValue,
  varyingColumn,
  previousIds,
  nextIds,
  organizationId,
}: SyncJunctionTableParams): Promise<void> {
  const toAdd = nextIds.filter((id) => !previousIds.includes(id));
  const toRemove = previousIds.filter((id) => !nextIds.includes(id));

  if (toRemove.length > 0) {
    const { error: delErr } = await supabase
      .from(table)
      .delete()
      .eq(fixedColumn, fixedValue)
      .in(varyingColumn, toRemove);
    if (delErr) throw delErr;
  }

  if (toAdd.length > 0 && organizationId) {
    const { error: insErr } = await supabase.from(table).insert(
      toAdd.map((varyingValue) => ({
        [fixedColumn]: fixedValue,
        [varyingColumn]: varyingValue,
        organization_id: organizationId,
      })),
    );
    if (insErr) throw insErr;
  }
}
